import type { KnowledgeDocument, KnowledgeDocumentStatus } from '../../types';

/**
 * Ingestion status as the User sees it (task 574).
 *
 * The vocabulary is the service's — `pending`, `indexing`, `ready`, `failed`
 * (kv, 2026-08-17 console review) — surfaced with a plain-language label so the
 * badge says what the document can do, not what the worker is doing. `failed`
 * carries its reason and the stage that produced it, because "the PDF has no
 * text layer" and "the embedding call failed" are different problems for the
 * User: one is their file, the other is ours.
 */
export const STATUS_LABELS: Record<KnowledgeDocumentStatus, string> = {
  pending: 'Queued',
  indexing: 'Indexing',
  ready: 'Ready',
  failed: 'Failed',
};

const STATUS_CLASSES: Record<KnowledgeDocumentStatus, string> = {
  pending: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
  indexing: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
  ready: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
  failed: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
};

const PHASE_LABELS: Record<string, string> = {
  extraction: 'Reading the file',
  chunking: 'Splitting the text',
  embedding: 'Indexing for search',
};

interface KnowledgeStatusBadgeProps {
  document: Pick<KnowledgeDocument, 'status' | 'failureReason' | 'failurePhase'>;
  /** Show the failure reason under the badge. Off in dense table cells. */
  showFailureReason?: boolean;
}

export function KnowledgeStatusBadge({
  document,
  showFailureReason = true,
}: KnowledgeStatusBadgeProps) {
  const { status, failureReason, failurePhase } = document;
  const phaseLabel = failurePhase ? PHASE_LABELS[failurePhase] ?? failurePhase : null;

  return (
    <div>
      <span
        data-testid={`knowledge-status-${status}`}
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${STATUS_CLASSES[status]}`}
      >
        {STATUS_LABELS[status] ?? status}
      </span>
      {status === 'failed' && showFailureReason && (failureReason || phaseLabel) && (
        <p
          data-testid="knowledge-failure-reason"
          className="mt-1 text-xs text-red-600 dark:text-red-400"
        >
          {phaseLabel ? `${phaseLabel}: ` : ''}
          {failureReason ?? 'No reason recorded.'}
        </p>
      )}
    </div>
  );
}

export default KnowledgeStatusBadge;
